import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { useSearchParams } from 'react-router-dom';
import api, { languagesApi } from '../utils/api';
import { ALL_LANGUAGES, searchLanguages } from '../utils/languageList';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  User, 
  Moon, 
  Sun, 
  Languages, 
  Plus,
  Trash2,
  Loader2,
  Search,
  Check,
  ChevronsUpDown,
  Zap,
  CreditCard,
  History,
  Gift,
  Sparkles,
  BookOpen,
  Volume2,
  FileText
} from 'lucide-react';

export default function Settings() {
  const { user } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [existingLanguages, setExistingLanguages] = useState([]);
  const [userLanguages, setUserLanguages] = useState([]);
  const [selectedRole, setSelectedRole] = useState('studying');
  const [loading, setLoading] = useState(false);
  
  // Language selector state
  const [languagePopoverOpen, setLanguagePopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNewLang, setSelectedNewLang] = useState(null);

  // Credits state
  const [credits, setCredits] = useState({ balance: 0, total_purchased: 0, total_used: 0 });
  const [packages, setPackages] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditCosts, setCreditCosts] = useState({});
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [purchasingPackage, setPurchasingPackage] = useState(null);

  // Get active tab from URL params
  const activeTab = searchParams.get('tab') || 'profile';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [existingRes, userLangsRes] = await Promise.all([
          languagesApi.getAll(),
          languagesApi.getUserLanguages()
        ]);
        setExistingLanguages(existingRes.data);
        setUserLanguages(userLangsRes.data);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };

    fetchData();
  }, []);

  // Fetch credits data when credits tab is active
  useEffect(() => {
    if (activeTab === 'credits') {
      fetchCreditsData();
    }
  }, [activeTab]);

  const fetchCreditsData = async () => {
    setLoadingCredits(true);
    try {
      const [balanceRes, packagesRes, historyRes, costsRes] = await Promise.all([
        api.get('/credits/balance'),
        api.get('/credits/packages'),
        api.get('/credits/history?limit=50'),
        api.get('/credits/costs')
      ]);
      setCredits(balanceRes.data);
      setPackages(packagesRes.data.packages || []);
      setCreditHistory(historyRes.data.transactions || []);
      setCreditCosts(costsRes.data.costs || {});
    } catch (err) {
      console.error('Error fetching credits:', err);
      toast.error(t('settings.credits.loadError'));
    } finally {
      setLoadingCredits(false);
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    setPurchasingPackage(packageId);
    try {
      // Initiate purchase
      const purchaseRes = await api.post('/credits/purchase', {
        package_id: packageId,
        payment_method: 'stripe' // Default to stripe for now
      });

      // For testing, auto-confirm the purchase
      // In production, this would redirect to Stripe/PayPal
      const confirmRes = await api.post(`/credits/confirm-test/${purchaseRes.data.purchase_id}`);
      
      toast.success(`Successfully added ${confirmRes.data.credits_added} credits!`);
      
      // Refresh credits data
      fetchCreditsData();
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.response?.data?.detail || 'Failed to purchase credits');
    } finally {
      setPurchasingPackage(null);
    }
  };

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  // Filter languages based on search
  const filteredLanguages = useMemo(() => {
    if (!searchQuery) return ALL_LANGUAGES;
    return searchLanguages(searchQuery);
  }, [searchQuery]);

  // Languages already added by user (both from DB and user_languages)
  const userLangCodes = useMemo(() => {
    const codes = new Set();
    userLanguages.forEach(ul => {
      if (ul.language?.code) codes.add(ul.language.code.toLowerCase());
    });
    return codes;
  }, [userLanguages]);

  const handleAddLanguage = async () => {
    if (!selectedNewLang) {
      toast.error(t('settings.languages.selectError'));
      return;
    }

    setLoading(true);
    try {
      let languageId = null;
      
      // First, check if language exists in DB (case-insensitive search)
      const existingLang = existingLanguages.find(
        l => l.code?.toLowerCase() === selectedNewLang.code.toLowerCase()
      );

      if (existingLang) {
        // Language already exists in system - just add to user's study list
        languageId = existingLang.language_id;
      } else {
        // Language doesn't exist in local cache - try to find it in DB
        try {
          const lookupRes = await languagesApi.getByCode(selectedNewLang.code);
          if (lookupRes.data?.language_id) {
            languageId = lookupRes.data.language_id;
            // Update local cache
            setExistingLanguages(prev => [...prev, lookupRes.data]);
          }
        } catch (lookupErr) {
          // Language doesn't exist in system
          // Only admins can create new languages - show appropriate message
          toast.error(`"${selectedNewLang.name}" is not available in the system. Contact an admin to add it.`);
          setLoading(false);
          return;
        }
      }

      if (!languageId) {
        toast.error(t('settings.languages.missingError'));
        setLoading(false);
        return;
      }

      // Add to user's languages (study list)
      await languagesApi.addUserLanguage({
        language_id: languageId,
        role: selectedRole
      });
      
      // Refresh user languages
      const res = await languagesApi.getUserLanguages();
      setUserLanguages(res.data);
      setSelectedNewLang(null);
      setSearchQuery('');
      setLanguagePopoverOpen(false);
      toast.success(`Added ${selectedNewLang.name} to your study list`);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to add language';
      if (errorMessage.includes('already added')) {
        toast.error(`${selectedNewLang.name} is already in your study list`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLanguage = async (languageId) => {
    // First fetch a preview so we can warn the user about cascade deletions
    // (AI-generated texts, word progress, forge lessons) BEFORE they commit.
    let preview = null;
    try {
      const res = await languagesApi.previewUserLanguageDeletion(languageId);
      preview = res.data;
    } catch (err) {
      // Preview failed — fall back to a simple confirm. Better than silent.
      console.error('delete-preview failed:', err);
    }

    const lines = [];
    if (preview) {
      lines.push(
        t('settings.languages.deleteConfirmHeader', { name: preview.language_name })
      );
      lines.push('');
      if (preview.ai_generated_texts > 0) {
        lines.push(
          t('settings.languages.deleteConfirmAiTexts', { count: preview.ai_generated_texts })
        );
      }
      if (preview.imported_texts > 0) {
        lines.push(
          t('settings.languages.deleteConfirmImportedTexts', { count: preview.imported_texts })
        );
      }
      if (preview.word_states > 0) {
        lines.push(
          t('settings.languages.deleteConfirmWordStates', { count: preview.word_states })
        );
      }
      if (preview.forge_lesson_progress > 0) {
        lines.push(
          t('settings.languages.deleteConfirmForgeLessons', { count: preview.forge_lesson_progress })
        );
      }
      lines.push('');
      lines.push(t('settings.languages.deleteConfirmProceed'));
    } else {
      lines.push(t('settings.languages.deleteConfirmFallback'));
    }

    if (!window.confirm(lines.join('\n'))) return;

    try {
      await languagesApi.removeUserLanguage(languageId);
      setUserLanguages(prev => prev.filter(ul => ul.language_id !== languageId));
      toast.success(t('settings.languages.removed'));
    } catch (err) {
      toast.error(t('settings.languages.removeError'));
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('settings.subtitle')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6" data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="h-4 w-4 mr-2" />
            {t('settings.tabs.account')}
          </TabsTrigger>
          <TabsTrigger value="languages" data-testid="tab-languages">
            <Languages className="h-4 w-4 mr-2" />
            {t('settings.tabs.languages')}
          </TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">
            <Zap className="h-4 w-4 mr-2" />
            {t('settings.tabs.credits')}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('settings.account.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-xl">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-lg font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="appearance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                {t('settings.account.appearance')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="dark-mode">{t('settings.account.darkMode')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.account.useDarkTheme')}
                  </p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={isDark}
                  onCheckedChange={toggleTheme}
                  data-testid="dark-mode-toggle"
                />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="color-legend-card">
            <CardHeader>
              <CardTitle>{t('settings.strengthLegend.title')}</CardTitle>
              <CardDescription>
                Words are colored based on their mastery level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#EF4444' }} />
                  <span className="text-sm">{t('settings.strengthLegend.new')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#F97316' }} />
                  <span className="text-sm">{t('settings.strengthLegend.learning')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#A855F7' }} />
                  <span className="text-sm">{t('settings.strengthLegend.familiar')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#8B5CF6' }} />
                  <span className="text-sm">{t('settings.strengthLegend.known')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-foreground" />
                  <span className="text-sm">{t('settings.strengthLegend.mastered')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Languages Tab */}
        <TabsContent value="languages" className="space-y-6">
          <Card data-testid="languages-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-5 w-5" />
                {t('settings.languages.title')}
              </CardTitle>
              <CardDescription>
                {t('settings.languages.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col md:flex-row gap-3">
                <Popover open={languagePopoverOpen} onOpenChange={setLanguagePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={languagePopoverOpen}
                      className="flex-1 justify-between"
                      data-testid="add-language-select"
                    >
                      {selectedNewLang ? (
                        <span>
                          {selectedNewLang.name} ({selectedNewLang.code})
                          {selectedNewLang.native_name && selectedNewLang.native_name !== selectedNewLang.name && (
                            <span className="text-muted-foreground ml-1">
                              - {selectedNewLang.native_name}
                            </span>
                          )}
                        </span>
                      ) : (
                        t('settings.languages.selectLanguage')
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder={t('settings.languages.search')}
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                      />
                      <CommandList>
                        <CommandEmpty>{t('settings.languages.noLanguageFound')}</CommandEmpty>
                        <CommandGroup>
                          <ScrollArea className="h-[300px]">
                            {filteredLanguages.map(lang => {
                              const isAdded = userLangCodes.has(lang.code.toLowerCase());
                              return (
                                <CommandItem
                                  key={lang.code}
                                  value={`${lang.code} ${lang.name} ${lang.native_name || ''}`}
                                  disabled={isAdded}
                                  onSelect={() => {
                                    if (!isAdded) {
                                      setSelectedNewLang(lang);
                                      setLanguagePopoverOpen(false);
                                    }
                                  }}
                                  className="flex items-center justify-between"
                                >
                                  <div>
                                    <span className="font-medium">{lang.name}</span>
                                    <span className="text-muted-foreground ml-2 text-sm">
                                      [{lang.code}]
                                    </span>
                                    {lang.native_name && lang.native_name !== lang.name && (
                                      <span className="text-muted-foreground ml-2 text-sm">
                                        {lang.native_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {lang.direction === 'rtl' && (
                                      <Badge variant="outline" className="text-xs">RTL</Badge>
                                    )}
                                    {isAdded && (
                                      <Check className="h-4 w-4 text-green-500" />
                                    )}
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </ScrollArea>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-32" data-testid="language-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studying">Studying</SelectItem>
                    <SelectItem value="native">Native</SelectItem>
                    <SelectItem value="interface">Interface</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleAddLanguage} 
                  disabled={loading || !selectedNewLang}
                  className="gap-2"
                  data-testid="add-language-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-sm font-medium">Your Languages ({userLanguages.length})</h4>
                {userLanguages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No languages added yet. Search and add a language to start learning!
                  </p>
                ) : (
                  <div className="space-y-2">
                    {userLanguages.map(ul => (
                      <div 
                        key={ul.id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                        data-testid={`user-language-${ul.language_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-medium">{ul.language?.name}</p>
                            <p className="text-sm text-muted-foreground">
                              [{ul.language?.code}]
                              {ul.language?.native_name && ul.language.native_name !== ul.language.name && (
                                <span className="ml-1">{ul.language.native_name}</span>
                              )}
                            </p>
                          </div>
                          <Badge variant="outline">{ul.role}</Badge>
                          {ul.language?.direction === 'rtl' && (
                            <Badge variant="secondary">RTL</Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveLanguage(ul.language_id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`remove-language-${ul.language_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credits Tab */}
        <TabsContent value="credits" className="space-y-6">
          {loadingCredits ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Current Balance Card */}
              <Card data-testid="credits-balance-card" className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Current Balance</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-amber-600 dark:text-amber-400">
                          {credits.balance.toLocaleString()}
                        </span>
                        <span className="text-lg text-muted-foreground">credits</span>
                      </div>
                    </div>
                    <div className="h-16 w-16 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Zap className="h-8 w-8 text-amber-500" />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 pt-4 border-t border-amber-500/20">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Purchased</p>
                      <p className="text-lg font-semibold">{credits.total_purchased.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Used</p>
                      <p className="text-lg font-semibold">{credits.total_used.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Purchase Credits */}
              <Card data-testid="credits-purchase-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Add Credits
                  </CardTitle>
                  <CardDescription>
                    Choose a credit package to enhance your learning experience
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {packages.map((pkg, index) => (
                      <div
                        key={pkg.id}
                        className={`relative p-4 rounded-lg border-2 transition-all hover:border-primary/50 ${
                          index === 2 ? 'border-amber-500 bg-amber-500/5' : 'border-border'
                        }`}
                        data-testid={`credit-package-${pkg.id}`}
                      >
                        {index === 2 && (
                          <Badge className="absolute -top-2 right-2 bg-amber-500 text-white">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Popular
                          </Badge>
                        )}
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-semibold">{pkg.label}</h4>
                            {pkg.bonus > 0 && (
                              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                <Gift className="h-3 w-3" />
                                +{pkg.bonus} bonus credits
                              </p>
                            )}
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold">${pkg.price.toFixed(2)}</span>
                            <span className="text-xs text-muted-foreground">USD</span>
                          </div>
                          <Button
                            className="w-full"
                            variant={index === 2 ? 'default' : 'outline'}
                            onClick={() => handlePurchaseCredits(pkg.id)}
                            disabled={purchasingPackage === pkg.id}
                            data-testid={`buy-${pkg.id}`}
                          >
                            {purchasingPackage === pkg.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Plus className="h-4 w-4 mr-2" />
                            )}
                            {purchasingPackage === pkg.id ? 'Processing...' : 'Buy Now'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Payment processing via Stripe. Secure and encrypted.
                  </p>
                </CardContent>
              </Card>

              {/* Credit Costs Reference */}
              <Card data-testid="credits-costs-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Credit Usage Guide
                  </CardTitle>
                  <CardDescription>
                    How credits are used across features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Core Learning */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Core Learning</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                          <BookOpen className="h-4 w-4 text-emerald-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Word Learning</p>
                            <p className="font-semibold text-amber-500">2 credits</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                          <Search className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Dictionary</p>
                            <p className="font-semibold text-amber-500">1 credit</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                          <Volume2 className="h-4 w-4 text-purple-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">TTS Audio</p>
                            <p className="font-semibold text-amber-500">5 credits</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                          <FileText className="h-4 w-4 text-orange-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Text Import</p>
                            <p className="font-semibold text-amber-500">10 credits</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Free Features */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Free Features</h4>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-full">Forge Path Exercises</span>
                        <span className="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-full">Text Reading</span>
                        <span className="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-full">Mark Words as Ignored</span>
                        <span className="px-2 py-1 text-xs bg-amber-500/10 text-amber-500 rounded-full">Biblical Languages (Hebrew, Greek, Syriac)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Transaction History */}
              <Card data-testid="credits-history-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Activity History
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Your recent credit usage and purchases
                  </p>
                </CardHeader>
                <CardContent>
                  {creditHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No transactions yet. Purchase credits to get started!
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {creditHistory.map((tx) => {
                        // Determine icon based on description
                        const getActivityIcon = (desc) => {
                          if (!desc) return <Zap className="h-4 w-4" />;
                          const d = desc.toLowerCase();
                          if (d.includes('word learning')) return <BookOpen className="h-4 w-4" />;
                          if (d.includes('dictionary') || d.includes('lookup')) return <Search className="h-4 w-4" />;
                          if (d.includes('tts') || d.includes('audio')) return <Volume2 className="h-4 w-4" />;
                          if (d.includes('text import') || d.includes('import')) return <FileText className="h-4 w-4" />;
                          if (d.includes('generation') || d.includes('topic')) return <Sparkles className="h-4 w-4" />;
                          if (d.includes('purchase') || d.includes('bought')) return <CreditCard className="h-4 w-4" />;
                          if (d.includes('free') || d.includes('bonus')) return <Gift className="h-4 w-4" />;
                          return tx.amount > 0 ? <Plus className="h-4 w-4" /> : <Zap className="h-4 w-4" />;
                        };
                        
                        return (
                          <div
                            key={tx.transaction_id}
                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-800/30 transition-colors"
                            data-testid={`transaction-${tx.transaction_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                tx.amount > 0 
                                  ? 'bg-green-500/10 text-green-500' 
                                  : 'bg-amber-500/10 text-amber-500'
                              }`}>
                                {getActivityIcon(tx.description)}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{tx.description || tx.transaction_type}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(tx.created_at).toLocaleDateString()} at {new Date(tx.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                              </div>
                            </div>
                            <div className={`font-semibold ${
                              tx.amount > 0 ? 'text-green-600' : 'text-amber-500'
                            }`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
